/* eslint-disable sonarjs/prefer-single-boolean-return */
/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, filter, reaction, withAssign } from '@reatom/framework'
import { match } from 'ts-pattern'

import { Array, AsyncStateN, Order, pipe } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import {
  pairRegistry,
  reatomPairInfoBulkSubscription,
} from '@root/entities/pair/atoms'
import { blacklistCurrenciesModel } from '@root/features/blacklist/blacklist-currencies/atoms'
import { reatomQuickBuyQuoteBulkSubscription } from '@root/features/quote/atoms'
import {
  cookingPairsEvent,
  newPairsEvent,
  trendingPairsEvent,
} from '@root/features/realtime/atoms'
import { reatomDataTable } from '@root/shared/ui-kit'

import { ExploreSorting } from '../../model'
import { ExploreFeedType } from '../../model/explore-feed-type'
import * as ExploreFilter from '../../model/explore-filter'
import { createExploreTableColumns } from '../../ui/explore-table/explore-table-columns'
import {
  exploreTablePeriodAtom,
  mapToQuickBuySection,
} from '../explore-table/reatom-explore-table-params'
import { tableExploreFiltersAtom } from '../reatom-explore-filters'

import type { ExploreTableColumnId } from '../../ui/explore-table/explore-column'
import type { ExploreTableContext } from '../../ui/explore-table/explore-table-types'
import type { ExploreTableFeedType } from '../explore-table/reatom-explore-table-params'
import type { Result } from '@blade/std-lib'
import type { Atom, AtomState, Ctx } from '@reatom/framework'
import type { Network } from '@root/entities/network'
import type { PairMetrics, PairModel } from '@root/entities/pair'
import type { PairEventPayload } from '@root/features/realtime/atoms'
import type { UnknownError } from '@root/shared/core/Error'
import type { TableData } from '@root/shared/ui-kit'
import type { TablePagination } from '@root/shared/ui-kit/data-table/atoms/reatom-data-table-pagination'

const mapSortingColumnToDto = (column: ExploreTableColumnId) => {
  return match(column)
    .returnType<ExploreSorting.ExploreSortingField | undefined>()
    .with('liquidity', () => 'liquidityInUsd')
    .with('volume', () => 'volumeInUsd')
    .with('volume-net', () => 'netVolumeInUsd')
    .with('holders-change', () => 'holdersChangePercents')
    .with('holders', () => 'holdersCount')
    .with('market-cap', () => 'marketCapUsd')
    .with('swaps', () => 'txCount')
    .with('createdAt', () => 'createdAt')
    .otherwise(() => undefined)
}

type LoadArgs = {
  pagination: TablePagination
  sorting:
    | {
        sortBy: ExploreSorting.ExploreSortingField
        sortDir: 'asc' | 'desc'
      }
    | undefined
  params: {
    filters: ExploreFilter.ExploreFilter
    network: Network.Network
    period: PairMetrics.MetricsSpan
  }
}
type Args = {
  feedType: ExploreTableFeedType
  loadPairs: (
    ctx: Ctx,
    args: LoadArgs,
  ) => Promise<Result.Result<TableData<PairModel>, UnknownError>>
  /**
   * Something outside the filters that also decides what the rows are.
   *
   * Trending has one: which ranking it is showing. The filters and the period
   * already reload the table this way, and a list ordered by another rule is
   * as much a different answer as a different filter is.
   */
  reloadOn?: Atom<unknown>
}

export const reatomExploreTableBase = ({
  loadPairs,
  feedType,
  reloadOn,
}: Args) => {
  const pausedAtom = atom(false, 'pausedAtom')
  const pairsBufferAtom = atom<Array<PairModel>>([], 'pairsBufferAtom')
  const pairInfoSubscriptionModel = reatomPairInfoBulkSubscription({
    name: 'explore-table',
  })
  const quickBuyQuoteSubscriptionBulkModel =
    reatomQuickBuyQuoteBulkSubscription({
      subscriberKey: 'explore-table',
      section: mapToQuickBuySection(feedType),
      autoSubscribe: false,
    })

  const paramsAtom = atom((ctx) => {
    const network = ctx.spy(selectedNetworkAtom)
    return { network }
  }, 'paramsAtom').pipe(filter())

  const getId = (model: PairModel) => model.address.toString()

  const dtoSorting = atom((ctx) => {
    const sorting = ctx.spy(tableModel.sorting)
    if (!sorting) {
      return undefined
    }
    const sortBy = mapSortingColumnToDto(sorting.sortColumn)
    if (!sortBy) {
      return undefined
    }
    return {
      sortBy,
      sortDir: sorting.sortDirection,
    }
  })

  const getSortingOrderAction = action((ctx) => {
    const sorting = ctx.get(dtoSorting)
    if (!sorting) {
      return undefined
    }
    return ExploreSorting.sortingToOrder(
      sorting,
      ctx.get(exploreTablePeriodAtom),
    )(ctx)
  })

  const sortedStateAtom = atom(
    (ctx): AsyncStateN.AsyncStateN<unknown, TableData<PairModel>> => {
      const state = ctx.spy(tableModel)
      const sorting = ctx.spy(dtoSorting)

      if (!sorting) {
        return state
      }

      const period = ctx.spy(exploreTablePeriodAtom)
      const order = ExploreSorting.sortingToOrder(sorting, period)(ctx)

      if (!order) {
        return state
      }

      return pipe(
        state,
        AsyncStateN.map((it) => {
          const items = it.data
          const sorted = pipe(items, Array.sort(order))
          return { ...it, data: sorted }
        }),
      )
    },
  )

  const visiblePairsStateAtom = atom((ctx) => {
    const state = ctx.spy(sortedStateAtom)
    const blacklistedAddresses = ctx.spy(
      blacklistCurrenciesModel.blacklistedAddresses,
    )
    return state.pipe(
      AsyncStateN.map((state) => {
        const withMigrated = state.data.map((it) => {
          const migratedPair = ctx.spy(it.migratedPair)
          if (migratedPair) {
            return migratedPair
          }
          return it
        })

        const visibleItems = withMigrated.filter(
          (it) => !blacklistedAddresses.has(it.quote.address),
        )

        return { ...state, data: visibleItems }
      }),
    )
  })

  const tableModel = reatomDataTable<
    PairModel,
    ExploreTableColumnId,
    AtomState<typeof paramsAtom>,
    ExploreTableContext
  >({
    columns: createExploreTableColumns({
      sortable: true,
    }),
    name: 'exploreDataTable',
    loadItems: async (ctx, { sorting, pagination, params: { network } }) => {
      pairsBufferAtom(ctx, [])

      const period = ctx.get(exploreTablePeriodAtom)
      const filters = ctx.get(tableExploreFiltersAtom)

      const dtoSorting = (() => {
        if (!sorting) {
          return undefined
        }
        const sortBy = mapSortingColumnToDto(sorting.sortColumn)

        if (!sortBy) {
          return undefined
        }

        return {
          sortBy,
          sortDir: sorting.sortDirection,
        }
      })()

      const result = ctx.schedule(() =>
        loadPairs(ctx, {
          sorting: dtoSorting,
          pagination,
          params: { filters, network, period },
        }),
      )

      pairsBufferAtom(ctx, [])

      return result
    },
    limit: 20,
    paramsAtom,
    overrideState: visiblePairsStateAtom,
    onConnectStrategy: 'none',
    getId,
    // A launch is pushed onto this list the moment it lands, and nothing took
    // anything off the far end. The column shows a page at a time; keeping ten
    // of them is already more than anyone scrolls back through.
    maxItems: 200,
  })

  const appendPairsAction = action((ctx, pairs: Array<PairModel>) => {
    tableModel.upsertItems(ctx, pairs, { insertMode: 'prepend' })
  })

  const isPairInSortingBounds = (ctx: Ctx, pair: PairModel) => {
    const state = ctx.get(sortedStateAtom)
    if (!AsyncStateN.isSuccess(state)) {
      return false
    }

    const sortingOrder = getSortingOrderAction(ctx)
    const sorting = ctx.get(tableModel.sorting)

    if (!sorting || !sortingOrder) {
      return true
    }
    const items = state.value.data

    if (items.length === 1) {
      return true
    }

    const firstPair = items.at(0)
    const lastPair = items.at(-1)
    if (!firstPair || !lastPair) {
      return true
    }

    if (sorting.sortDirection === 'asc') {
      if (Order.isLessThanOrEqualTo(sortingOrder)(pair, firstPair)) {
        return true
      }
      return false
    }

    // Order here is flipped
    if (Order.isLessThanOrEqualTo(sortingOrder)(pair, lastPair)) {
      return true
    }
    return false
  }

  const handlePairEventsAction = action((ctx, payload: PairEventPayload) => {
    const state = ctx.get(sortedStateAtom)
    if (!AsyncStateN.isSuccess(state)) {
      return
    }
    if (payload.updateType !== 'append') {
      return
    }
    const pairModels = payload.pairs.map((it) =>
      pairRegistry.resolveFromDto(ctx, it),
    )
    const exploreFilter = ctx.get(tableExploreFiltersAtom)
    const period = ctx.get(exploreTablePeriodAtom)

    // TODO: Realtime min max age filters
    const filteredPairModels = pairModels.filter(
      (it) =>
        ExploreFilter.isPairSatisfiesExploreFilter(
          exploreFilter,
          period,
          new Date(),
        )(ctx, it) && isPairInSortingBounds(ctx, it),
    )

    const paused = ctx.get(pausedAtom)
    if (paused) {
      pairsBufferAtom(ctx, (current) => [...current, ...filteredPairModels])
      return
    }

    appendPairsAction(ctx, filteredPairModels)
  })

  const filtersReaction = reaction((ctx) => {
    const _filters = ctx.spy(tableExploreFiltersAtom)

    tableModel.load(ctx)
  })

  const reloadOnReaction = reaction((ctx) => {
    if (!reloadOn) {
      return
    }
    ctx.spy(reloadOn)
    tableModel.load(ctx)
  })

  const periodReaction = reaction((ctx) => {
    const _period = ctx.spy(exploreTablePeriodAtom)
    const appliedFiltersCount = ctx.get(
      tableExploreFiltersAtom.appliedFiltersCount,
    )
    const sorting = ctx.get(tableModel.sorting)
    // If no filters or sorting, no need to refresh, we just change values in cells
    if (appliedFiltersCount === 0 && !sorting) {
      return
    }
    tableModel.load(ctx)
  })

  const pausedReaction = reaction((ctx) => {
    const paused = ctx.spy(pausedAtom)
    if (paused) {
      return
    }
    const buffer = ctx.get(pairsBufferAtom)
    appendPairsAction(ctx, buffer)
    pairsBufferAtom(ctx, [])
  })

  const initializeAction = action((ctx) => {
    const pausedSub = pausedReaction(ctx)
    const filtersSub = filtersReaction(ctx)
    const periodSub = periodReaction(ctx)
    const reloadOnSub = reloadOnReaction(ctx)

    const feedTypeEvent = match(feedType)
      .with(ExploreFeedType.New, () => newPairsEvent)
      .with(ExploreFeedType.Trending, () => trendingPairsEvent)
      .with(ExploreFeedType.Cooking, () => cookingPairsEvent)
      .exhaustive()

    const unsubscribePairEvents = feedTypeEvent.onCall((ctx, payload) => {
      handlePairEventsAction(ctx, payload)
    })

    const unsubPairInfoSubscription = tableModel.onChange((ctx, pairsState) => {
      if (AsyncStateN.isError(pairsState)) {
        pairInfoSubscriptionModel.unsubscribeAll(ctx)
        quickBuyQuoteSubscriptionBulkModel.unsubscribeAll(ctx)
        return
      }
      if (AsyncStateN.isSuccess(pairsState)) {
        pairInfoSubscriptionModel.processPairs(ctx, pairsState.value.data)
        quickBuyQuoteSubscriptionBulkModel.processPairs(
          ctx,
          pairsState.value.data,
        )
        return
      }
    })

    return () => {
      unsubscribePairEvents()
      unsubPairInfoSubscription()
      pausedSub.unsubscribe()
      filtersSub.unsubscribe()
      periodSub.unsubscribe()
      reloadOnSub.unsubscribe()
      pairInfoSubscriptionModel.unsubscribeAll(ctx)
      quickBuyQuoteSubscriptionBulkModel.unsubscribeAll(ctx)
    }
  })

  return tableModel.pipe(
    withAssign(() => ({
      paused: pausedAtom,
      buffedCount: atom((ctx) => ctx.spy(pairsBufferAtom).length),
      initialize: initializeAction,
    })),
  )
}

export type ExploreTableBaseModel = ReturnType<typeof reatomExploreTableBase>
